import React, { useState } from "react";
import { Box } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import moment from "moment-timezone";
import { formatAxisValue } from "./formatAxisValue";
import { getChartColor } from "visual-components/analytics/campaign/chartColors";

/**
 * Multi Line Chart Component using MUI X Charts
 * Displays multiple line series with date-based x-axis
 * @param {Object} props
 * @param {Array} props.items - Array of line items with name, title, and data
 * @param {Object} props.dataByDate - Data organized by date
 * @param {string} props.selectedTab - Currently selected tab
 * @returns {import("react").ReactElement}
 */
export default function MultiLineChart(props) {
  const { items = [], dataByDate = {} } = props;

  // Track hidden series via legend clicks
  const [legendHiddenMap, setLegendHiddenMap] = useState({});

  const dates = Object.keys(dataByDate);

  if (dates.length === 0) {
    return <Box sx={{ width: "100%", minHeight: 300 }} />;
  }

  // Create x-axis data as indices (will be formatted as dates)
  const xAxisData = dates.map((_, index) => index);

  // Calculate tick interval for x-axis to prevent label density issues
  // Show max ~10-12 labels regardless of data points
  const tickInterval = dates.length > 12 ? Math.ceil(dates.length / 10) : 1;

  // Create series for all items (not just selected) to maintain legend visibility
  /** @type {import("@mui/x-charts").LineSeries[]} */
  const series = items.map((item, index) => {
    const isHidden = legendHiddenMap[item.name];
    const baseColor = getChartColor(index);

    // Apply 40% opacity to color when hidden (for legend visual feedback)
    const displayColor = isHidden ? `${baseColor}66` : baseColor;

    return {
      id: item.name,
      data: isHidden ? item.data.map(() => null) : item.data.map((point) => point.y),
      label: item.title || item.name,
      color: displayColor,
      showMark: false,
      curve: "catmullRom", // Smoother line edges
      valueFormatter: (value) => (value !== null ? value?.toLocaleString() : "Hidden"),
      connectNulls: false,
    };
  });

  // Format dates for x-axis labels
  const formattedDates = dates.map((date) => moment.utc(date).format("MMM D"));

  // Handle legend item click to toggle series visibility
  const handleLegendClick = (_event, context) => {
    const seriesId = context.seriesId;
    if (seriesId) {
      setLegendHiddenMap((prev) => ({
        ...prev,
        [seriesId]: !prev[seriesId],
      }));
    }
  };

  return (
    <LineChart
      xAxis={[
        {
          data: xAxisData,
          scaleType: "point",
          valueFormatter: (value) => formattedDates[value] || "",
          tickLabelStyle: {
            fontSize: 11,
          },
          tickInterval: (_, index) => index % tickInterval === 0,
        },
      ]}
      yAxis={[
        {
          disableLine: true,
          valueFormatter: formatAxisValue,
        },
      ]}
      series={series}
      height={300}
      grid={{ horizontal: true }}
      onLineClick={handleLegendClick}
      slotProps={{
        legend: {
          position: {
            vertical: "bottom",
            horizontal: "start",
          },
          sx: {
            fontSize: 12,
          },
          onItemClick: handleLegendClick,
        },
      }}
    />
  );
}
